/**
 * Multi-Agent Execution Controller
 * Manages running executions, live state, and observability data.
 */

import type {
  AgentMetrics,
  CostSummary,
  ExecutionEvent,
} from "../../../../../src/multi-agent/observability/types";
import {
  ObservableExecutionState,
  ExecutionEventType,
  NodeStatus,
} from "../../../../../src/multi-agent/observability/types";

// ─── Types ────────────────────────────────────────────────────────────────────

export type ExecutionState = {
  executionId: string | null;
  profileId: string | null;
  profileName: string;
  state: ObservableExecutionState | null;
  agents: AgentMetrics[];
  events: ExecutionEvent[];
  costSummary: CostSummary | null;
  startTime: number | null;
  durationMs: number;
  isPaused: boolean;
  selectedNodeId: string | null;
  error: string | null;
};

export const INITIAL_EXECUTION_STATE: ExecutionState = {
  executionId: null,
  profileId: null,
  profileName: "",
  state: null,
  agents: [],
  events: [],
  costSummary: null,
  startTime: null,
  durationMs: 0,
  isPaused: false,
  selectedNodeId: null,
  error: null,
};

// ─── Execution Controller ──────────────────────────────────────────────────────

export class ExecutionController {
  private state: ExecutionState;
  private timer: ReturnType<typeof setInterval> | null = null;
  private listeners: Array<(state: ExecutionState) => void> = [];

  constructor() {
    this.state = { ...INITIAL_EXECUTION_STATE };
  }

  getState(): ExecutionState {
    return this.state;
  }

  subscribe(listener: (state: ExecutionState) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  private emit(): void {
    for (const listener of this.listeners) {
      listener(this.state);
    }
  }

  // ─── Start Execution ──────────────────────────────────────────────────────

  async startExecution(profileId: string, profileName: string): Promise<string | null> {
    const executionId = `exec-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    this.state = {
      ...INITIAL_EXECUTION_STATE,
      executionId,
      profileId,
      profileName,
      state: ObservableExecutionState.Pending,
      startTime: Date.now(),
    };
    this.emit();

    // Start duration timer
    this.timer = setInterval(() => {
      if (this.state.startTime && this.state.state === "running") {
        this.state = {
          ...this.state,
          durationMs: Date.now() - this.state.startTime,
        };
        this.emit();
      }
    }, 500);

    // Simulate execution initialization
    await this.simulateInit(executionId);

    return executionId;
  }

  private async simulateInit(executionId: string): Promise<void> {
    this.updateState({ state: ObservableExecutionState.Running });
    this.addEvent({
      id: `evt-${Date.now()}`,
      timestamp: Date.now(),
      type: ExecutionEventType.ExecutionStarted,
      executionId,
      message: "Execution started",
    });

    // Simulate agent nodes appearing
    await this.delay(300);
  }

  // ─── Update State ────────────────────────────────────────────────────────

  updateState(patch: Partial<ExecutionState>): void {
    this.state = { ...this.state, ...patch };
    this.emit();
  }

  // ─── Agent State Updates ─────────────────────────────────────────────────

  updateAgentMetrics(agent: AgentMetrics): void {
    const existing = this.state.agents.findIndex((a) => a.nodeId === agent.nodeId);
    if (existing >= 0) {
      const agents = [...this.state.agents];
      agents[existing] = agent;
      this.state = { ...this.state, agents };
    } else {
      this.state = {
        ...this.state,
        agents: [...this.state.agents, agent],
      };
    }
    this.emit();
  }

  setAgentState(
    agentId: string,
    nodeId: string,
    status: AgentMetrics["status"],
    extra: Partial<AgentMetrics> = {},
  ): void {
    const existing = this.state.agents.find((a) => a.nodeId === nodeId);
    if (existing) {
      this.updateAgentMetrics({ ...existing, status, ...extra });
    } else {
      this.updateAgentMetrics({
        agentId,
        nodeId,
        name: agentId,
        role: "worker",
        status,
        inputTokens: 0,
        outputTokens: 0,
        totalTokens: 0,
        cost: 0,
        toolCallCount: 0,
        errorCount: 0,
        warnings: [],
        messages: [],
        toolInvocations: [],
        ...extra,
      });
    }
  }

  // ─── Event Log ──────────────────────────────────────────────────────────

  addEvent(event: ExecutionEvent): void {
    this.state = {
      ...this.state,
      events: [...this.state.events.slice(-499), event],
    };
    this.emit();
  }

  // ─── Cost Tracking ──────────────────────────────────────────────────────

  updateCostSummary(summary: CostSummary): void {
    this.state = { ...this.state, costSummary: summary };
    this.emit();
  }

  // ─── Pause / Resume ────────────────────────────────────────────────────

  pause(): void {
    this.state = { ...this.state, isPaused: true };
    this.addEvent({
      id: `evt-${Date.now()}`,
      timestamp: Date.now(),
      type: ExecutionEventType.ExecutionPaused,
      executionId: this.state.executionId!,
      message: "Execution paused",
    });
    this.emit();
  }

  resume(): void {
    this.state = { ...this.state, isPaused: false };
    this.addEvent({
      id: `evt-${Date.now()}`,
      timestamp: Date.now(),
      type: ExecutionEventType.ExecutionResumed,
      executionId: this.state.executionId!,
      message: "Execution resumed",
    });
    this.emit();
  }

  // ─── Abort ──────────────────────────────────────────────────────────────

  abort(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    this.state = {
      ...this.state,
      state: ObservableExecutionState.Cancelled,
      isPaused: false,
    };
    this.addEvent({
      id: `evt-${Date.now()}`,
      timestamp: Date.now(),
      type: ExecutionEventType.ExecutionCancelled,
      executionId: this.state.executionId!,
      message: "Execution cancelled",
    });
    this.emit();
  }

  // ─── Complete ───────────────────────────────────────────────────────────

  complete(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    this.state = {
      ...this.state,
      state: ObservableExecutionState.Completed,
      isPaused: false,
    };
    this.addEvent({
      id: `evt-${Date.now()}`,
      timestamp: Date.now(),
      type: ExecutionEventType.ExecutionCompleted,
      executionId: this.state.executionId!,
      message: "Execution completed",
    });
    this.emit();
  }

  fail(error: string): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    this.state = {
      ...this.state,
      state: ObservableExecutionState.Failed,
      isPaused: false,
      error,
    };
    this.addEvent({
      id: `evt-${Date.now()}`,
      timestamp: Date.now(),
      type: ExecutionEventType.ExecutionFailed,
      executionId: this.state.executionId!,
      message: error,
      error,
    });
    this.emit();
  }

  // ─── Reset ──────────────────────────────────────────────────────────────

  reset(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    this.state = { ...INITIAL_EXECUTION_STATE };
    this.emit();
  }

  // ─── Export ─────────────────────────────────────────────────────────────

  exportAll(): {
    executionId: string;
    state: ObservableExecutionState | null;
    agents: AgentMetrics[];
    events: ExecutionEvent[];
    costSummary: CostSummary | null;
    durationMs: number;
    exportedAt: number;
  } {
    return {
      executionId: this.state.executionId ?? "unknown",
      state: this.state.state,
      agents: this.state.agents,
      events: this.state.events,
      costSummary: this.state.costSummary,
      durationMs: this.state.durationMs,
      exportedAt: Date.now(),
    };
  }

  // ─── Helpers ────────────────────────────────────────────────────────────

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

// ─── Singleton ────────────────────────────────────────────────────────────────

let _controller: ExecutionController | null = null;

export function getExecutionController(): ExecutionController {
  if (!_controller) {
    _controller = new ExecutionController();
  }
  return _controller;
}
